import { useState, useEffect, useRef } from 'react';
import { Box, Input, Sheet, CircularProgress } from '@mui/joy';
import { EditorContent, useEditor } from '@tiptap/react';
import { Document } from '@tiptap/extension-document';
import { Paragraph } from '@tiptap/extension-paragraph';
import { Text } from '@tiptap/extension-text';
import { Bold } from '@tiptap/extension-bold';
import { Italic } from '@tiptap/extension-italic';
import { Heading } from '@tiptap/extension-heading';
import { BulletList } from '@tiptap/extension-bullet-list';
import { OrderedList } from '@tiptap/extension-ordered-list';
import { ListItem } from '@tiptap/extension-list-item';
import { Table } from '@tiptap/extension-table';
import { TableRow } from '@tiptap/extension-table-row';
import { TableCell } from '@tiptap/extension-table-cell';
import { TableHeader } from '@tiptap/extension-table-header';
import { Link } from '@tiptap/extension-link';
import { Image } from '@tiptap/extension-image';
import { History } from '@tiptap/extension-history';
import { EditorToolbar } from './EditorToolbar';
import type { Page } from '../../../types/pages';

interface PageEditorProps {
  page: Page;
  onUpdate: (updates: Partial<Page>) => void;
  isLoading: boolean;
}

export function PageEditor({ page, onUpdate, isLoading }: PageEditorProps) {
  const [title, setTitle] = useState(page.title);

  // Separate debounce refs for title and content to prevent interference
  const contentDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const titleDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Track page switching to only sync content on page change, not on save responses
  const initialLoadDone = useRef(false);
  const lastPageId = useRef<string | null>(null);

  // Initialize TipTap editor
  const editor = useEditor({
    extensions: [
      Document,
      Paragraph,
      Text,
      Bold,
      Italic,
      Heading.configure({
        levels: [1, 2],
      }),
      BulletList,
      OrderedList,
      ListItem,
      Table.configure({
        resizable: true,
      }),
      TableRow,
      TableCell,
      TableHeader,
      Link.configure({
        openOnClick: false,
        HTMLAttributes: {
          target: '_blank',
          rel: 'noopener noreferrer',
        },
      }),
      Image,
      History,
    ],
    content: page.content,
    onUpdate: ({ editor }) => {
      // Debounce content updates with separate ref
      if (contentDebounceRef.current) {
        clearTimeout(contentDebounceRef.current);
      }
      contentDebounceRef.current = setTimeout(() => {
        onUpdate({ content: editor.getJSON() });
      }, 800);
    },
  });

  // Only sync content from server on page switch, not on every page.content change
  // This prevents keystroke loss when save responses update the cache
  useEffect(() => {
    if (editor && !editor.isDestroyed) {
      // Reset when switching to a different page
      if (page.id !== lastPageId.current) {
        lastPageId.current = page.id;
        initialLoadDone.current = false;
      }

      // Only set content on initial load of this page
      if (!initialLoadDone.current && page.content) {
        editor.commands.setContent(page.content);
        initialLoadDone.current = true;
      }
    }
  }, [editor, page.id, page.content]);

  // Update title when page changes
  useEffect(() => {
    setTitle(page.title);
  }, [page.title]);

  // Handle title change with separate debounce ref
  const handleTitleChange = (newTitle: string) => {
    setTitle(newTitle);
    // Debounce title updates with separate ref to avoid interfering with content saves
    if (titleDebounceRef.current) {
      clearTimeout(titleDebounceRef.current);
    }
    titleDebounceRef.current = setTimeout(() => {
      onUpdate({ title: newTitle });
    }, 500);
  };

  // Clean up both debounce refs on unmount
  useEffect(() => {
    return () => {
      if (contentDebounceRef.current) clearTimeout(contentDebounceRef.current);
      if (titleDebounceRef.current) clearTimeout(titleDebounceRef.current);
    };
  }, []);

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
      {/* Title Input */}
      <Box sx={{ p: { xs: 1.5, md: 3 }, pb: 0 }}>
        <Input
          value={title}
          onChange={(e) => handleTitleChange(e.target.value)}
          placeholder="Page Title"
          variant="plain"
          sx={{
            fontSize: { xs: '1.5rem', md: '2rem' },
            fontWeight: 'bold',
            '--Input-focusedHighlight': 'none',
            '&::before': { display: 'none' },
          }}
        />
      </Box>

      {/* Toolbar */}
      {editor && (
        <Box sx={{ px: { xs: 1.5, md: 3 }, py: 2 }}>
          <EditorToolbar editor={editor} />
        </Box>
      )}

      {/* Editor Content */}
      <Sheet
        sx={{
          flex: 1,
          overflow: 'auto',
          px: { xs: 1.5, md: 3 },
          pb: 3,
          '& .ProseMirror': {
            minHeight: '400px',
            outline: 'none',
            fontSize: '1rem',
            lineHeight: 1.6,
            '& > * + *': {
              marginTop: '0.75em',
            },
            '& h1': {
              fontSize: '2em',
              fontWeight: 'bold',
              marginTop: '1em',
              marginBottom: '0.5em',
            },
            '& h2': {
              fontSize: '1.5em',
              fontWeight: 'bold',
              marginTop: '1em',
              marginBottom: '0.5em',
            },
            '& p': {
              marginBottom: '1em',
            },
            '& ul, & ol': {
              paddingLeft: '1.5rem',
              marginBottom: '1em',
            },
            '& li': {
              marginBottom: '0.25em',
            },
            '& a': {
              color: 'var(--joy-palette-primary-500)',
              textDecoration: 'underline',
              cursor: 'pointer',
            },
            '& img': {
              maxWidth: '100%',
              height: 'auto',
              margin: '1em 0',
            },
            '& table': {
              borderCollapse: 'collapse',
              tableLayout: 'fixed',
              width: '100%',
              margin: '1em 0',
              overflow: 'hidden',
            },
            '& th, & td': {
              border: '1px solid var(--joy-palette-divider)',
              padding: '0.5rem',
              position: 'relative',
            },
            '& th': {
              backgroundColor: 'var(--joy-palette-background-level1)',
              fontWeight: 'bold',
            },
            '& .selectedCell': {
              backgroundColor: 'var(--joy-palette-primary-100)',
            },
          },
        }}
      >
        <EditorContent editor={editor} />
      </Sheet>
    </Box>
  );
}